import {
  useCallback, useEffect, useState
} from 'react';

import useSWR from 'swr';

import fetchPipelineDeploymentStatus from '../../../utils/api_functions/pipelineStatusRequest';

export const usePipelineStatus = ({
  deploymentName, deploymentId,
}) => {
  const [lastRefreshTime, setLastRefreshTime] = useState(undefined);
  const [data, setData] = useState({});
  const [error, setError] = useState(null);
  const [isLoading, setLoading] = useState(false);

  const fetchStatus = useCallback(() => {
    setLoading(true);
    setLastRefreshTime(new Date());
    fetchPipelineDeploymentStatus(deploymentId, deploymentName).then((res) => {
      setData(res);
      setError(null);
      setLoading(false);
    }).catch((err) => {
      setError(err?.message ? err.message : err);
      setData({});
      setLoading(false);
    })
  }, [deploymentId, deploymentName]);

  useEffect(() => {
    if (!lastRefreshTime) {
      fetchStatus();
    }
  })

  return {
    data, error, loading: isLoading, lastRefreshTime, refetch: fetchStatus
  }
}

export const usePipelineStatusPolling = ({
  deploymentName, deploymentId,
}) => {
  const [lastRefreshTime, setLastRefreshTime] = useState(undefined);
  const [isUndeployDelayActive, setIsUndeployDelayActive] = useState(false);

  const [statusData, setStatusData] = useState();

  const {
    data,
    error,
    isLoading,
    mutate,
  } = useSWR(['pipeline-deployment-status', deploymentId, deploymentName], () => fetchPipelineDeploymentStatus(deploymentId, deploymentName), {
    refreshInterval: 3000,
    loadingTimeout: 1000,
  });

  const status = statusData?.status;
  const isStarting = status === 'Starting';
  const isRunning = status === 'Running' && !error;
  const isFullyUndeployed = !isUndeployDelayActive && error;
  const isDeploymentTransitioning = isUndeployDelayActive || isStarting;

  const refetch = useCallback(() => {
    setLastRefreshTime(new Date());
    mutate();
  }, [mutate]);

  const activateUndeployDelay = () => {
    setIsUndeployDelayActive(true);
  }

  useEffect(() => {
    if (isUndeployDelayActive) {
      if (error) {
        setIsUndeployDelayActive(false);
      }
    }
  }, [error, isUndeployDelayActive])

  useEffect(() => {
    setLastRefreshTime(new Date());
    if (error) {
      setStatusData(null);
    } else {
      setStatusData(data);
    }
  }, [data, error, status])

  return {
    data: statusData,
    error,
    status,
    isLoading,
    isStarting,
    isRunning,
    isDeploymentTransitioning,
    isFullyUndeployed,
    refetch,
    activateUndeployDelay,
    lastRefreshTime,
  }
}

export default usePipelineStatus;
